import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Building2, ChevronDown, Check, Plus, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export const FirmSwitcher = () => {
  const { user, updateUser } = useAuth();
  const [firms, setFirms] = useState([]);
  const [activeFirmId, setActiveFirmId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    loadFirms();
  }, []);

  const loadFirms = async () => {
    try {
      const response = await api.get('/auth/firms');
      setFirms(response.data.firms || []);
      setActiveFirmId(response.data.active_firm_id);
    } catch (error) {
      console.error('Failed to load firms:', error);
      // Fallback to single firm from user context
      if (user?.firm_id) {
        setFirms([{
          firm_id: user.firm_id,
          name: user.firm_name || 'My Firm',
          role: user.role,
          is_active: true
        }]);
        setActiveFirmId(user.firm_id);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchFirm = async (firmId) => {
    if (firmId === activeFirmId || switching) return;
    
    setSwitching(true);
    try {
      const response = await api.post('/auth/switch-firm', { firm_id: firmId });
      
      // Update token
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      
      // Update user context
      if (response.data.user && updateUser) {
        updateUser(response.data.user);
      }
      
      setActiveFirmId(firmId);
      toast.success(response.data.message || `Switched to ${response.data.firm?.name}`);
      
      // Reload the page to refresh all data for the new firm context
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch firm:', error);
      toast.error(error.response?.data?.detail || 'Failed to switch firm');
    } finally {
      setSwitching(false);
    }
  };

  // Don't render if only one firm
  if (loading || firms.length <= 1) {
    return null;
  }

  const activeFirm = firms.find(f => f.firm_id === activeFirmId) || firms[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="gap-2 px-3 py-2 h-auto hover:bg-slate-100"
          data-testid="firm-switcher-btn"
          disabled={switching}
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Building2 className="h-4 w-4 text-white" />
            </div>
            <div className="text-left hidden sm:block">
              <p className="text-sm font-medium text-slate-900 max-w-[120px] truncate">
                {activeFirm?.name || 'Select Firm'}
              </p>
              <p className="text-xs text-slate-500 capitalize">
                {activeFirm?.role?.replace('_', ' ')}
              </p>
            </div>
          </div>
          {switching ? (
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <div className="px-2 py-1.5">
          <p className="text-xs font-medium text-slate-500 uppercase">Your Firms</p>
        </div>
        <DropdownMenuSeparator />
        {firms.map((firm) => (
          <DropdownMenuItem
            key={firm.firm_id}
            onClick={() => handleSwitchFirm(firm.firm_id)}
            className={cn(
              "flex items-center gap-3 px-2 py-2 cursor-pointer",
              firm.firm_id === activeFirmId && "bg-blue-50"
            )}
            data-testid={`firm-option-${firm.firm_id}`}
          >
            <div className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center",
              firm.firm_id === activeFirmId 
                ? "bg-gradient-to-br from-blue-500 to-indigo-600" 
                : "bg-slate-200"
            )}>
              <Building2 className={cn(
                "h-4 w-4",
                firm.firm_id === activeFirmId ? "text-white" : "text-slate-500"
              )} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{firm.name}</p>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-500 capitalize">
                  {firm.role?.replace('_', ' ')}
                </span>
                {firm.plan && firm.plan !== 'trial' && (
                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                    {firm.plan}
                  </Badge>
                )}
              </div>
            </div>
            {firm.firm_id === activeFirmId && (
              <Check className="h-4 w-4 text-blue-600" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default FirmSwitcher;
